import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../shared/forms';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRoleEnum, useUserContext } from '../../shared/contexts';
import { Environment } from '../../shared/environment';
import getValidationErrors from '../../shared/helpers/getValidationErrors';
import { buildAddress } from '../../shared/helpers/buildAddress';
import { LayoutShell, LayoutRegister } from '../../shared/layouts';
import { ICreateBusiness, ICreateBusinessAdmin, IUserView, businessAdminService, userService, businessService, feedback } from '../../shared/services';
import { CreateBusinessFormBody, createBusinessFormSchema, Forbidden, IDaySchedule, createDefaultWeekSchedule, scheduleToApiDto } from '../../shared/components';
import { ValidationError } from 'yup';

export const CreateBusiness: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<IUserView>();
  const [schedule, setSchedule] = useState<IDaySchedule[]>(() => createDefaultWeekSchedule());


  const formRef = useRef<FormHandles>(null);

  const { userRoles } = useUserContext();
  const navigate = useNavigate();

  const { data: users } = useQuery(['users'], () =>
    userService.get(true),
  );

  const { mutate: createBusinessAdmin, isLoading: isLoadingCreateBusinessAdmin } = useMutation((businessAdmin: ICreateBusinessAdmin) =>
    businessAdminService.create(businessAdmin), {
    onSuccess: () => {
      feedback('Usuário gerado!', 'success');
      navigate('/home');
    },
    onError: (error: Error) => {
      feedback(error.message, 'error');
    },
  });

  const { mutate: createBusiness, isLoading: isLoadingCreateBusiness } = useMutation((business: ICreateBusiness) =>
    businessService.create(business), {
    onSuccess: (businessId: number) => {
      if (selectedUser) {
        const businessAdmin: ICreateBusinessAdmin = {
          userId: selectedUser.id,
          businessId: businessId,
        };
        createBusinessAdmin(businessAdmin);
      }
    },
    onError: (error: Error) => {
      feedback(error.message, 'error');
    },
  });

  const handleSubmit = useCallback(async (data: Record<string, unknown>) => {
    try {
      formRef.current?.setErrors({});

      await createBusinessFormSchema.validate({ name: data.name, street: data.street, streetNumber: data.streetNumber, neighborhood: data.neighborhood, city: data.city, zipCode: data.zipCode, cnpj: data.cnpj, phone: data.phone, instagram: data.instagram, facebook: data.facebook, website: data.website }, { abortEarly: false });

      if (selectedUser) {
        const address = buildAddress(data);

        const business: ICreateBusiness = {
          name: data.name as string,
          address,
          phone: (data.phone as string) || undefined,
          instagram: (data.instagram as string) || undefined,
          facebook: (data.facebook as string) || undefined,
          website: (data.website as string) || undefined,
          paymentMethods: data.paymentMethods as string | undefined,
          weeklySchedules: scheduleToApiDto(schedule),
          cnpj: data.cnpj as string | undefined,
        };

        createBusiness(business);
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      }
    }
  }, [selectedUser, schedule]);

  return userRoles.includes(UserRoleEnum.SystemAdmin) ? (
    <LayoutShell>
      <Form ref={formRef} onSubmit={handleSubmit}>
        <LayoutRegister
          isLoading={isLoadingCreateBusinessAdmin || isLoadingCreateBusiness}
          saveButtonLabel='Criar estabelecimento'
          isSubmit
        >
          <Box
            display='flex'
            flexDirection='column'
            gap={3}
            padding={3}
          >
            <Typography fontWeight={600}>Dados do administrador do business:</Typography>

            <Autocomplete
              options={users || []}
              getOptionLabel={(opt) => opt.name + ' (' + (opt.email || opt.phone) + ')'}
              noOptionsText={Environment.emptyListInput}
              onChange={(_, value) => value && setSelectedUser(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Administrador do estabelecimento'
                  variant='outlined'
                  placeholder={Environment.defaultSearchInput}
                />
              )}
            />

            <CreateBusinessFormBody
              schedule={schedule}
              onScheduleChange={setSchedule}
            />
          </Box>
        </LayoutRegister>
      </Form>
    </LayoutShell>
  ) : (
    <Forbidden />
  );
};
